import { z } from 'zod'
import { widgetSchema } from '../../common/schema'

// Filter for the bulk feed-availability endpoint. Plain object (not a
// widgetFilterSchema.extend): no single `property` — this widget renders many
// properties at once, so the inherited field was dead (#748 retired it).
export const multiCalendarFilterSchema = z.object({
  feed_id: z.number().int().describe('Feed ID'),
  platform: z.string().nullish().describe('Optional platform UUID to scope properties'),
}).describe('Data filter options for the multi-property calendar')

export type MultiCalendarFilter = z.infer<typeof multiCalendarFilterSchema>

const multiCalLight = {
  accent: '#f59e0c',
  foreground: '#333333',
  mutedForeground: '#999999',
  border: '#e5e5e5',
  headerBackground: '#f7f7f8',
  rowBackground: '#ffffff',
  availableBackground: '#e4f4d9',
  availableColor: '#2f6b2f',
  bookedBackground: '#fdd2d2',
  bookedColor: '#881616',
  priceColor: '#666666',
  selectedBackground: '#b7db9f',
  selectedColor: '#1f3d15',
  hoverBackground: '#d3ecc4',
  disabledBackground: '#ececec',
  noticeBackground: '#fef3cd',
  noticeColor: '#8a6d00',
}

const multiCalDark = {
  accent: '#f59e0c',
  foreground: '#e0e0e0',
  mutedForeground: '#888888',
  border: '#3a3a3a',
  headerBackground: '#222222',
  rowBackground: '#1a1a1a',
  availableBackground: '#22351f',
  availableColor: '#9bd67f',
  bookedBackground: '#3b1d1d',
  bookedColor: '#e88',
  priceColor: '#aaaaaa',
  selectedBackground: '#4a7a32',
  selectedColor: '#e4f4d9',
  hoverBackground: '#3a5f28',
  disabledBackground: '#2c2c2c',
  noticeBackground: '#3a3320',
  noticeColor: '#e6c34d',
}

const multiCalColorSchema = (d: typeof multiCalLight) => z.object({
  accent: z.string().default(d.accent).describe('Accent color'),
  foreground: z.string().default(d.foreground).describe('Primary text color'),
  mutedForeground: z.string().default(d.mutedForeground).describe('Secondary text color'),
  border: z.string().default(d.border).describe('Grid borders'),
  headerBackground: z.string().default(d.headerBackground).describe('Header row background'),
  rowBackground: z.string().default(d.rowBackground).describe('Property row background'),
  availableBackground: z.string().default(d.availableBackground).describe('Available day cell background'),
  availableColor: z.string().default(d.availableColor).describe('Available day cell text/price color'),
  bookedBackground: z.string().default(d.bookedBackground).describe('Booked/unavailable day cell background'),
  bookedColor: z.string().default(d.bookedColor).describe('Booked/unavailable day cell text color'),
  priceColor: z.string().default(d.priceColor).describe('Price text color'),
  selectedBackground: z.string().default(d.selectedBackground).describe('Selected date-range cell background'),
  selectedColor: z.string().default(d.selectedColor).describe('Selected date-range cell text/price color'),
  hoverBackground: z.string().default(d.hoverBackground).describe('Hover preview cell background while picking the checkout date'),
  disabledBackground: z.string().default(d.disabledBackground).describe('Cell background for dates disabled during selection, e.g. inside the min-stay window'),
  noticeBackground: z.string().default(d.noticeBackground).describe('Error/notice banner background'),
  noticeColor: z.string().default(d.noticeColor).describe('Error/notice banner text color'),
})

export const multiCalendarWidgetConfig = widgetSchema.extend({
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('Availability').describe('Section title text'),
  }).default({ show: false, title: 'Availability' }).describe('Section heading shown above the widget content'),
  filter: multiCalendarFilterSchema,
  daysToShow: z.number().int().min(7).max(92).default(30).describe('Number of days shown in the grid (endpoint caps the range at 92)'),
  perPage: z.number().int().min(1).max(50).default(20).describe('Properties loaded per page'),
  startDate: z.string().nullish().describe('First day of the grid (YYYY-MM-DD). Defaults to today.'),
  hidePrice: z.boolean().default(false).describe('Hide nightly prices in the day cells'),
  currency: z.string().default('USD').describe('Currency code for the day-cell prices (WP: MultiCalendar currency)'),
  showThumbnail: z.boolean().default(true).describe('Show the property thumbnail in the first column'),
  stickyHeader: z.boolean().default(true).describe('Keep the date header and property column pinned while scrolling'),
  // WP parity: MultiCalendar.php's `calendar_height` shortcode attr (#686). A
  // constrained CSS-length contract — value + unit — so it can't emit invalid
  // CSS. The `none` unit removes the cap (grid grows to fit). Default 70vh
  // reproduces the prior hardcoded `.hr-multical-scroll { max-height: 70vh }`.
  maxHeight: z.object({
    value: z.number().min(1).default(70).describe('Maximum height value (ignored when unit is "none")'),
    unit: z.enum(['vh', 'px', 'rem', 'none']).default('vh').describe('Unit for the maximum height; "none" removes the cap so the grid grows to fit'),
  }).default({ value: 70, unit: 'vh' }).describe('Cap the scrollable grid height before it scrolls internally (WP: calendar_height)'),
  propertyLinkable: z.boolean().default(true).describe('Link each property row to its property page'),
  allowSelectDates: z.boolean().default(false).describe('Let visitors click to select a check-in/check-out range on a property row, opening a Reserve/Clear popup. Off keeps the grid read-only.'),
  showClearButton: z.boolean().default(true).describe('Show a Clear Dates button while a selection is active (requires allowSelectDates)'),
  reserveButtonText: z.string().default('Reserve').describe('Label for the Reserve action in the selection popup'),
  clearButtonText: z.string().default('Clear Dates').describe('Label for the Clear Dates action/button'),
  noItemsText: z.string().nullish().default('No properties found.').describe('Message shown when the feed has no properties'),
  lightModeColors: multiCalColorSchema(multiCalLight).default(multiCalLight).describe('Colors in light mode'),
  darkModeColors: multiCalColorSchema(multiCalDark).default(multiCalDark).describe('Colors in dark mode'),
})

export type MultiCalendarWidgetConfig = z.infer<typeof multiCalendarWidgetConfig>
